(() => {
  const STORAGE_KEY = 'ratniva-delivery-pincode';

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'long' }).format(date);
  }

  function getEstimate(payload) {
    const data = payload?.data || payload || {};
    const date = data.estimated_delivery_date || data.expected_delivery_date || data.eta;
    if (date && formatDate(date)) return formatDate(date);

    const tat = data.tat_days || data.expected_tat || data.tat;
    const days = Number.parseInt(tat, 10);
    if (Number.isFinite(days) && days >= 0) {
      const deliveryDate = new Date();
      deliveryDate.setDate(deliveryDate.getDate() + days);
      return formatDate(deliveryDate);
    }

    return '';
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('.delivery-estimate__form');
      this.input = this.querySelector('.delivery-estimate__input');
      this.button = this.querySelector('.delivery-estimate__button');
      this.status = this.querySelector('.delivery-estimate__status');
      this.result = this.querySelector('.delivery-estimate__result');
      this.destination = this.querySelector('.delivery-estimate__destination');
      this.date = this.querySelector('.delivery-estimate__date');

      if (!this.form || !this.input || !this.button || !this.status || !this.result) return;

      this.buttonLabel = this.button.textContent.trim();
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
        this.result.hidden = true;
        this.clearError();
      });

      try {
        const savedPincode = localStorage.getItem(STORAGE_KEY);
        if (/^\d{6}$/.test(savedPincode || '')) this.input.value = savedPincode;
      } catch (_error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      const pincode = this.input.value.trim();

      if (!/^\d{6}$/.test(pincode)) {
        this.showError('Please enter a correct PIN code.');
        return;
      }

      this.setLoading(true);
      this.result.hidden = true;
      this.clearError();

      try {
        if (!this.dataset.endpoint || !this.dataset.originPincode) {
          throw new Error('Delivery estimates are not configured yet.');
        }

        const url = new URL(this.dataset.endpoint, window.location.origin);
        url.searchParams.set('origin_pin', this.dataset.originPincode);
        url.searchParams.set('destination_pin', pincode);
        url.searchParams.set('mot', this.dataset.mode || 'E');

        const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.message || 'Delivery is unavailable for this PIN code.');

        const estimate = getEstimate(payload);
        if (!estimate) throw new Error('Delivery estimate is unavailable for this PIN code.');

        this.destination.textContent = `Delivery to ${pincode}`;
        this.date.innerHTML = `Estimated delivery: <strong>${estimate}</strong>`;
        this.result.hidden = false;

        try {
          localStorage.setItem(STORAGE_KEY, pincode);
        } catch (_error) {
          // The estimate still works when storage is unavailable.
        }
      } catch (error) {
        console.error('Delivery estimate unavailable', error);
        this.showError(error.message || 'Delivery estimate is temporarily unavailable. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.button.disabled = isLoading;
      this.button.textContent = isLoading ? 'Checking...' : this.buttonLabel;
      this.setAttribute('aria-busy', String(isLoading));
    }

    showError(message) {
      this.result.hidden = true;
      this.status.textContent = message;
      this.status.classList.add('delivery-estimate__status--error');
      this.input.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      this.status.textContent = '';
      this.status.classList.remove('delivery-estimate__status--error');
      this.input.removeAttribute('aria-invalid');
    }
  }

  if (!customElements.get('delivery-estimate')) {
    customElements.define('delivery-estimate', DeliveryEstimate);
  }
})();
